import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HealthMetric, getStatusColor } from "@/utils/deviceHealth";
import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";

interface HealthMetricCardProps {
  metric: HealthMetric;
  showRecommendations?: boolean;
}

export const HealthMetricCard = ({ 
  metric, 
  showRecommendations = true 
}: HealthMetricCardProps) => {
  const statusColor = getStatusColor(metric.status);
  
  const getStatusIcon = () => {
    switch (metric.status) {
      case "excellent":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "good":
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case "fair":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "poor":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };
  
  const getStatusVariant = () => {
    switch (metric.status) {
      case "excellent":
        return "default";
      case "good":
        return "secondary";
      case "fair":
        return "outline";
      case "poor":
        return "destructive";
      default:
        return "outline";
    }
  };

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader className="pb-3">
        <GlassCardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            {metric.name}
          </div>
          <Badge variant={getStatusVariant()}>
            {metric.status}
          </Badge>
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Score</span>
            <span className="font-medium">{metric.score}/{metric.maxScore}</span>
          </div>
          <Progress 
            value={(metric.score / metric.maxScore) * 100} 
            className="h-2"
            style={{
              // @ts-ignore
              '--progress-color': statusColor
            } as React.CSSProperties}
          />
        </div>
        
        <p className="text-sm text-muted-foreground">
          {metric.description}
        </p>
        
        {showRecommendations && metric.recommendations && metric.recommendations.length > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside text-sm space-y-1">
                {metric.recommendations.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </GlassCardContent>
    </GlassCard>
  );
};